<template>
  <BottomSheet :open="props.open" @close="$emit('close-modal')">
    <template #header>
      <div class="flex gap-2 items-center">
        <Icon name="Pencil" class="h-4 w-4 text-info" />
        <h3 class="font-semibold text-lg text-strongest">Edit Investment</h3>
      </div>
      <Button variant="paper-primary" size="sm" @click="$emit('close-modal')">
        <template #icon>
          <Icon name="X" class="h-4 w-4 text-cta-tertiary-outline-icon" />
        </template>
      </Button>
    </template>

    <div class="p-6 flex flex-col gap-6">
      <!-- Amount -->
      <div class="flex flex-col gap-2">
        <p class="font-inter text-sm font-medium text-strong">Investment Amount</p>
        <div class="relative">
          <div
            class="rounded-[100px] flex items-center h-14 p-[.875rem] transition-colors bg-container-bg"
            :class="amountError ? 'border border-info' : ''"
          >
            <Button @click="changeInvestmentAmount('decrease')" size="sm" variant="paper-primary">
              <template #icon>
                <Icon name="Minus" class="h-4 w-4" :stroke-width="3" />
              </template>
            </Button>
            <div class="relative flex items-center flex-1 justify-center">
              <span
                v-if="!isEditingAmount"
                class="font-inter font-medium text-center tabular-nums text-lg text-strongest cursor-text"
                @click="startEditingAmount"
              >
                ${{ formattedDisplayAmount }}
              </span>
              <input
                v-else
                ref="amountInputRef"
                type="text"
                inputmode="numeric"
                :value="editingAmountValue"
                @beforeinput="blockNonNumericInput"
                @input="handleAmountInput"
                @blur="finishEditingAmount"
                @keydown.enter="finishEditingAmount"
                @keydown.escape="cancelEditingAmount"
                class="font-inter font-medium text-center tabular-nums w-full bg-transparent outline-none text-lg text-strongest"
              />
            </div>
            <Button @click="changeInvestmentAmount('increase')" size="sm" variant="paper-primary">
              <template #icon>
                <Icon name="Plus" class="h-4 w-4" :stroke-width="3" />
              </template>
            </Button>
          </div>
          <div
            v-if="amountError"
            class="absolute left-0 top-full my-2 text-info text-xs font-inter whitespace-nowrap flex items-center font-medium"
          >
            <Icon
              name="Asterisk"
              class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-info"
              :stroke-width="3"
            />
            {{ amountError }}
          </div>
        </div>
      </div>

      <!-- Interest Type -->
      <div class="flex flex-col gap-2 mt-2">
        <p class="font-inter text-sm font-medium text-strong">Interest Payment</p>
        <div class="flex flex-col gap-2">
          <div
            v-for="option in compoundingOptions"
            :key="option.value"
            @click="handleCompoundingChange(option.value)"
            class="cursor-pointer rounded-2xl bg-container-bg text-strongest px-4 py-3 transition-colors border-2"
            :class="
              purchaseFlowStore.current_investment?.is_compounded === option.value
                ? 'border-info'
                : 'border-transparent'
            "
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-inter text-sm font-medium">{{ option.label }}</p>
                <p v-if="option.description" class="font-inter text-light text-xs mt-0.5">
                  {{ option.description }}
                </p>
              </div>
              <Icon
                v-if="purchaseFlowStore.current_investment?.is_compounded === option.value"
                name="Check"
                class="h-5 w-5 text-info ml-3"
                :stroke-width="2.5"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Offering -->
      <div class="flex flex-col gap-2" v-if="!isTermAndYieldStep">
        <p class="font-inter text-sm font-medium text-strong">Term & Yield</p>
        <div class="flex flex-col gap-2">
          <div
            v-for="offering in filteredOfferings"
            :key="offering.id"
            @click="handleOfferingChange(offering)"
            class="cursor-pointer rounded-2xl bg-container-bg text-strongest px-4 py-3 transition-colors border-2"
            :class="
              purchaseFlowStore.selected_offering?.record_id === offering.record_id
                ? 'border-info'
                : 'border-transparent'
            "
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-inter text-sm font-medium">
                  {{ getOfferingTermAndInterestRateDisplay(offering) }}
                </p>
                <p class="font-inter text-light text-xs mt-0.5">{{ offering.type }} offering</p>
              </div>
              <Icon
                v-if="purchaseFlowStore.selected_offering?.record_id === offering.record_id"
                name="Check"
                class="h-5 w-5 text-info ml-3"
                :stroke-width="2.5"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        @click="$emit('close-modal')"
        variant="primary-secondary"
        class="w-full"
        :disabled="!!amountError"
      >
        <template #label>
          <p class="font-league">Done</p>
        </template>
      </Button>
    </template>
  </BottomSheet>
</template>

<script setup>
import { computed, ref, nextTick } from "vue";
import { usePurchaseFlowStore, useDataStore } from "@/stores";
import { Button, Icon, BottomSheet } from "@/components";
import { commaSeparateThousands } from "@/utilities";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();

defineEmits(["close-modal"]);

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
});

const isEditingAmount = ref(false);
const editingAmountValue = ref("");
const previousAmount = ref(null);
const amountInputRef = ref(null);
const boundaryError = ref(null);
let boundaryTimeout = null;

const compoundingOptions = [
  {
    value: false,
    label: "Interest paid monthly",
    description: "Receive interest payments each month",
  },
  {
    value: true,
    label: "Interest paid at maturity",
    description: "Interest compounds and is paid at the end",
  },
];

const isTermAndYieldStep = computed(() => {
  return purchaseFlowStore.currentStep?.name === "Term and Yield";
});

const formattedDisplayAmount = computed(() => {
  return commaSeparateThousands(parseAmount(purchaseFlowStore.current_investment?.amount));
});

const maximumAmount = computed(() => {
  if (!purchaseFlowStore.selected_rollover) return null;
  return Math.floor(purchaseFlowStore.selected_rollover.rollover_available / 1000) * 1000;
});

const minimumAmount = computed(() => {
  if (purchaseFlowStore.selected_rollover) return 1000;

  const offeringType = purchaseFlowStore.selected_offering?.type;

  if (offeringType === "Reg D") {
    return purchaseFlowStore.signedInvestmentStatus?.has_signed_regd ? 1000 : 25000;
  }

  if (offeringType === "S1") {
    if (purchaseFlowStore.selected_offering?.is_short_term) {
      return 1000;
    }
    return purchaseFlowStore.signedInvestmentStatus?.has_signed_s1 ? 1000 : 5000;
  }

  return 1000;
});

const amountError = computed(() => {
  const currentAmount = parseAmount(purchaseFlowStore.current_investment?.amount);
  if (!currentAmount) return boundaryError.value;

  if (currentAmount < minimumAmount.value) {
    return `${formatCurrency(minimumAmount.value)} minimum investment for this offering`;
  }

  if (maximumAmount.value && currentAmount > maximumAmount.value) {
    return `Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`;
  }

  return boundaryError.value;
});

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function showBoundaryError(message) {
  clearTimeout(boundaryTimeout);
  boundaryError.value = message;
  boundaryTimeout = setTimeout(() => {
    boundaryError.value = null;
  }, 3000);
}

const filteredOfferings = computed(() => {
  const selected = purchaseFlowStore.selected_offering;
  const currentOfferingType = selected?.type;

  if (!currentOfferingType || !dataStore.offerings) return [];

  const selectedTier = selected?.tier;
  const isShortTerm = selected?.is_short_term === true;

  return dataStore.offerings
    .filter((offering) => {
      if (offering.type !== currentOfferingType) return false;

      if (isShortTerm ? !offering.is_short_term : offering.is_short_term) return false;

      if (currentOfferingType === "Reg D") {
        if (selectedTier == null) return false;
        return offering.tier === selectedTier;
      }

      return true;
    })
    .sort((a, b) => {
      if (a.bond_term !== b.bond_term) return b.bond_term - a.bond_term;
      return b.interest_rate - a.interest_rate;
    });
});

function parseAmount(value) {
  if (!value) return 0;
  return parseInt(value.toString().replace(/[^0-9]/g, "")) || 0;
}

function startEditingAmount() {
  previousAmount.value = parseAmount(purchaseFlowStore.current_investment?.amount);
  editingAmountValue.value = commaSeparateThousands(previousAmount.value);
  isEditingAmount.value = true;

  nextTick(() => {
    amountInputRef.value?.focus();
    amountInputRef.value?.select();
  });
}

function blockNonNumericInput(event) {
  if (!event.data) return;
  if (/[^0-9]/.test(event.data)) {
    event.preventDefault();
  }
}

function handleAmountInput(event) {
  const numericOnly = event.target.value.replace(/[^0-9]/g, "");
  const numericValue = parseInt(numericOnly) || 0;
  editingAmountValue.value = commaSeparateThousands(numericValue);
}

function finishEditingAmount() {
  if (!isEditingAmount.value) return;

  const newAmount = parseAmount(editingAmountValue.value);

  if (newAmount >= 1000) {
    let roundedAmount = Math.ceil(newAmount / 1000) * 1000;

    if (roundedAmount < minimumAmount.value) {
      roundedAmount = minimumAmount.value;
      showBoundaryError(
        `${formatCurrency(minimumAmount.value)} minimum investment for this offering`
      );
    }
    if (maximumAmount.value && roundedAmount > maximumAmount.value) {
      roundedAmount = maximumAmount.value;
      showBoundaryError(`Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`);
    }

    purchaseFlowStore.current_investment.amount = commaSeparateThousands(roundedAmount);
  } else {
    purchaseFlowStore.current_investment.amount = commaSeparateThousands(previousAmount.value);
  }

  isEditingAmount.value = false;
  editingAmountValue.value = "";
  previousAmount.value = null;
}

function cancelEditingAmount() {
  purchaseFlowStore.current_investment.amount = commaSeparateThousands(previousAmount.value);
  isEditingAmount.value = false;
  editingAmountValue.value = "";
  previousAmount.value = null;
}

function changeInvestmentAmount(action) {
  const currentAmount = parseInt(
    purchaseFlowStore.current_investment?.amount?.toString().replace(/,/g, "") || "0"
  );

  if (action === "increase") {
    const newAmount = currentAmount + 1000;
    if (maximumAmount.value && newAmount > maximumAmount.value) {
      showBoundaryError(`Maximum of ${formatCurrency(maximumAmount.value)} available to reinvest`);
      return;
    }
    purchaseFlowStore.current_investment.amount = newAmount.toLocaleString();
  } else if (action === "decrease") {
    const newAmount = currentAmount - 1000;
    if (newAmount < minimumAmount.value) {
      showBoundaryError(
        `${formatCurrency(minimumAmount.value)} minimum investment for this offering`
      );
      return;
    }
    purchaseFlowStore.current_investment.amount = newAmount.toLocaleString();
  }
}

function getOfferingTermAndInterestRateDisplay(offering) {
  if (!offering) return "";

  const term =
    offering.bond_term >= 12 ? `${offering.bond_term / 12}-year` : `${offering.bond_term}-month`;

  return `${term} term · ${offering.interest_rate}% interest rate`;
}

function handleCompoundingChange(value) {
  if (purchaseFlowStore.current_investment) {
    purchaseFlowStore.current_investment.is_compounded = value;
  }
}

function handleOfferingChange(offering) {
  purchaseFlowStore.selected_offering = offering;
}
</script>
